// Vendor-agnostic event tracking.
//
// Feature code calls `track('book_now_click', { salon_id })` and never touches
// gtag / dataLayer / any vendor SDK directly (design rule #9). This module is
// the single seam: it pushes a normalised event onto `window.dataLayer`, which
// GTM consumes and routes to GA4.
//
// Consent: events are only forwarded after the visitor has granted cookie
// consent. Before that, `track()` is a no-op to the network - nothing is
// pushed, no identifier leaves the browser. The moment consent flips to
// `granted` the analytics loaders (gtm.ts / ga.ts) inject GTM, and subsequent
// track() calls flow through. We intentionally do NOT buffer pre-consent
// events: a denied visitor must leave no analytics trace.
//
// IMPORTANT for callers: the user-facing action (open booking, place call,
// navigate) must never depend on track() succeeding. track() is a fire-and-
// forget side effect - call it, then do the action regardless.

import { consent } from '../consent'

/** The canonical event names. One enum so GA4 config and code can't drift. */
export type AnalyticsEvent =
  // Tier 1 - conversions
  | 'book_now_click'
  | 'salon_selected'
  | 'booking_handoff'
  | 'call_salon_click'
  | 'checkout_handoff'
  // Tier 2 - high intent
  | 'salon_picker_open'
  | 'call_salon_open'
  | 'add_to_cart'
  | 'concierge_open'
  | 'concierge_autoopen'
  | 'concierge_message'
  | 'concierge_feedback'
  | 'concierge_menu_select'
  | 'concierge_menu_link'
  // Tier 3 - content engagement
  | 'service_view'
  | 'trichology_view'
  | 'reel_play'
  | 'reel_complete'
  | 'product_view'
  | 'guide_view'
  // Tier 4 - secondary
  | 'newsletter_signup'
  | 'contact_form_submit'
  | 'photo_consent_submit'
  | 'cart_add_item'
  | 'cart_remove_item'
  | 'faq_open'
  // Tier 5 - consent telemetry
  | 'consent_granted'
  | 'consent_denied'

/** Event parameters. Flat, primitive values only - GA4 custom dimensions. */
export type EventParams = Record<string, string | number | boolean | undefined>

interface DataLayerWindow extends Window {
  dataLayer?: Array<Record<string, unknown>>
}

/**
 * Push an event to the dataLayer, gated on consent.
 *
 * @param event  one of the canonical AnalyticsEvent names
 * @param params optional flat parameters (salon_id, source, value, …)
 */
export function track(event: AnalyticsEvent, params: EventParams = {}): void {
  // Consent gate. Tier-5 consent events are the sole exception: they describe
  // the consent decision itself and must record the moment consent is granted
  // (denied is handled separately - see trackConsent below).
  if (consent.get() !== 'granted' && event !== 'consent_granted') return

  const w = window as DataLayerWindow
  w.dataLayer = w.dataLayer ?? []

  // Drop undefined params so GA4 doesn't receive empty dimensions.
  const clean: Record<string, unknown> = { event }
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) clean[k] = v
  }
  w.dataLayer.push(clean)
}

/**
 * Record the consent decision itself.
 *
 * `granted` rides through `track()` (which now passes its gate). `denied`
 * cannot - by definition there's no consented analytics to receive it - so we
 * surface it as a return signal callers may forward to a cookieless counter if
 * one is ever added. Today it is a deliberate no-op-to-GA, keeping a declined
 * visitor analytics-free while still centralising the decision in one place.
 */
export function trackConsent(decision: 'granted' | 'denied'): void {
  if (decision === 'granted') {
    track('consent_granted')
  }
  // decision === 'denied': intentionally not forwarded to GA4.
}
